import Vector from "victor";

export default class Rect {
  min: Vector;
  max: Vector;

  constructor(a: Vector, b: Vector) {
    this.min = new Vector(Math.min(a.x, b.x), Math.min(a.y, b.y));
    this.max = new Vector(Math.max(a.x, b.x), Math.max(a.y, b.y));
  }

  static fromCenter(center: Vector, size: Vector): Rect {
    return new Rect(center, center).inset(size.clone().multiplyScalar(-0.5));
  }

  static fromBoundingClientRect(r: {
    x: number;
    y: number;
    width: number;
    height: number;
  }): Rect {
    return new Rect(
      new Vector(r.x, r.y),
      new Vector(r.x + r.width, r.y + r.height)
    );
  }

  contains(p: Vector) {
    if (p.x < this.min.x) return false;
    if (p.y < this.min.y) return false;
    if (p.x > this.max.x) return false;
    if (p.y > this.max.y) return false;
    return true;
  }

  translated(p: Vector): Rect {
    return new Rect(this.min.clone().add(p), this.max.clone().add(p));
  }

  inset(p: Vector): Rect {
    return new Rect(this.min.clone().add(p), this.max.clone().subtract(p));
  }

  intersects(other: Rect): boolean {
    if (this.minX > other.maxX) return false;
    if (this.minY > other.maxY) return false;
    if (this.maxX < other.minX) return false;
    if (this.maxY < other.minY) return false;
    return true;
  }

  expand(point: Vector) {
    if (point.x < this.min.x) this.min.x = point.x;
    if (point.y < this.min.y) this.min.y = point.y;
    if (point.x > this.max.x) this.max.x = point.x;
    if (point.y > this.max.y) this.max.y = point.y;
  }

  isEqualTo(other: Rect) {
    return this.min.isEqualTo(other.min) && this.max.isEqualTo(other.max);
  }

  get size(): Vector {
    return this.max.clone().subtract(this.min);
  }

  get minX(): number {
    return this.min.x;
  }

  get minY(): number {
    return this.min.y;
  }

  get maxX(): number {
    return this.max.x;
  }

  get maxY(): number {
    return this.max.y;
  }

  get width(): number {
    return this.size.x;
  }

  get height(): number {
    return this.size.y;
  }

  get centerX(): number {
    return this.minX + this.width / 2;
  }

  get centerY(): number {
    return this.minY + this.height / 2;
  }
}
